import React from "react";
import classes from "./CurrencyBalanceCard.module.css";
import CurrencyBadge from "./CurrencyBadge";
import { currencySymbol } from "../utils/formatters";

/**
 * One currency's account: every figure on the card is denominated in it, and the card is badged
 * with it once.
 *
 * ## Why the card is the currency, not the metric
 *
 * A seller on PostmanAL holds two balances that are never converted into each other and never
 * added together. The first attempt rendered the second one as a small line beneath the first,
 * which read as a footnote on a single number. The second gave every *metric* its own card, so
 * `Detyrimi` and `Qarkullimi` each appeared twice, once per currency — four cards saying two
 * things, and the reader had to check a badge on each to know which was which.
 *
 * Grouping by currency instead means the badge is answered once and the card below it is wholly
 * one account: obligation, then turnover, in identical type. The two cards sit side by side at
 * equal width and equal weight, which is the layout that matches the data.
 *
 * ## Why there is no chart
 *
 * `CustomLineChartCard` — which these cards replaced on the client screen — draws a hardcoded
 * six-point series that is byte-identical for every client and every currency. It took 73% of the
 * card width to say nothing, and that width is what the second currency needed. `DriverDetails`
 * still uses that component; this one is deliberately figures-only.
 */
export type BalanceLine = {
	value?: number | string | null;
	label?: string;
};

/** One metric within the card — a headline figure and the two figures that explain it. */
export type BalanceSection = {
	title?: string;
	/** The headline balance. */
	value?: number | string | null;
	/** The supporting figures beneath the rule. */
	lines?: BalanceLine[];
};

export type CurrencyBalanceCardProps = {
	/** Currency every figure on this card is denominated in. */
	currencyId?: number | null;
	/** One block per metric, in the order they read down the card. */
	sections?: BalanceSection[];
	className?: string;
};

const format = (value?: number | string | null, currencyId?: number | null) => {
	if (
		value === null ||
		value === undefined ||
		value.toString().trim() === ""
	) {
		return "-";
	}
	const amount = Number(value);
	if (!Number.isFinite(amount)) return "-";

	return `${new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	}).format(amount)} ${currencySymbol(currencyId)}`;
};

const CurrencyBalanceCard: React.FC<CurrencyBalanceCardProps> = ({
	currencyId,
	sections = [],
	className,
}) => (
	<div className={`${classes.card} ${className ?? ""}`}>
		{sections.map((section, index) => (
			<div className={classes.section} key={index}>
				<div className={classes.header}>
					<p className={classes.title}>{section.title}</p>
					{/* Once per card, not once per metric: the whole card is this currency,
					    and a badge repeated beside every figure starts to read as a
					    qualifier on that figure alone. */}
					{index === 0 && <CurrencyBadge currencyId={currencyId} />}
				</div>

				<p className={classes.primary}>
					{format(section.value, currencyId)}
				</p>

				<div className={classes.rule} />

				<div className={classes.lines}>
					{(section.lines ?? []).map((line, lineIndex) => (
						<p className={classes.line} key={lineIndex}>
							<span className={classes.lineValue}>
								{format(line.value, currencyId)}
							</span>{" "}
							<span className={classes.lineLabel}>
								{line.label}
							</span>
						</p>
					))}
				</div>
			</div>
		))}
	</div>
);

export default CurrencyBalanceCard;
